import {
  getCatalogProductByGuidV1,
  getCatalogProductsV1,
  getCatalogSearchSuggestionsV1,
  getCatalogRelatedProductsV1,
  getCatalogRelatedProductsForBagV1,
  getStocksV1,
} from '@/services/platform/api/catalog/catalog';
import type {
  AppSettingsContentResponseModel,
  BrandContentResponseModel,
  CategoryContentResponseModel,
  CollectionContentResponseModel,
  CpiStockDto,
  GetCatalogProductsV1Params,
  GetCatalogSearchSuggestionsV1Params,
  GetStocksV1Params,
  ProductDetailDto,
  ProductListItemDto,
  ProductSearchResultDto,
  SearchSuggestionsDto,
  SectionContentResponseModel,
  SectionItemElementContentModel,
  SectionItemsElementContentModel,
  ShopIntroContentResponseModel,
  ShopPopularSearchesElementContentModel,
  ShopSectionContentResponseModel,
  SubCategoryContentResponseModel,
  SubSubCategoryContentResponseModel,
} from '@/services/platform/api/model';
import {
  SectionItemElementContentModelContentType,
  SectionItemsElementContentModelContentType,
  ShopSectionContentResponseModelContentType,
  SubCategoryContentResponseModelContentType,
  SubSubCategoryContentResponseModelContentType,
} from '@/services/platform/api/model';
import { getContentById, getContents, getPage } from '@/services/platform/data/cms';

/**
 * Fetches a paginated list of products from the local catalogue.
 *
 * Pagination is offset-based — use `page` and `pageSize` to navigate, and
 * `hasNextPage` / `totalPages` on the returned `products` (a
 * `ProductListItemDtoPagedResult`) to determine whether more pages exist.
 *
 * @param params - Optional query parameters (`GetCatalogProductsV1Params`):
 *   - `section`    — Filter by gender/section (multi-value)
 *   - `category`   — Filter by category (multi-value)
 *   - `brand`      — Filter by brand/vendor (multi-value)
 *   - `collection` — Filter by collection (multi-value)
 *   - `slugs`      — Filter to specific product slugs (multi-value)
 *   - `priceMin`   — Minimum price filter
 *   - `priceMax`   — Maximum price filter
 *   - `sortBy`     — Sort key (`ProductSortKey`); meaning of each numeric value
 *     is unverified from this codebase, check the API contract
 *   - `page`       — Page number (1-based)
 *   - `pageSize`   — Items per page
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @param signal - Optional `AbortSignal` to cancel/time out the request.
 * @returns `ProductSearchResultDto` on success — the paginated `products`
 *   plus `categories`/`brands`/`colors` facets and `minPrice`/`maxPrice`
 *   bounds for the current filter set — or `undefined` on non-200 responses.
 */
export async function getCatalogProducts(
  params?: GetCatalogProductsV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<ProductSearchResultDto | undefined> {
  const response = await getCatalogProductsV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getCatalogProducts: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Free-text search suggestions over the catalogue, optionally scoped to a section.
 *
 * Returns the top 10 of each: matched products (by title or brand, with public GUIDs),
 * category/subcategory/subsubcategory tag slugs (by tag value, usable directly as
 * `/products` filters), and collections (by title, with the Umbraco node key GUID
 * accepted by the `/products` `collection` filter). Taxonomy and collections are ranked
 * by how many active products in scope carry them.
 *
 * @param params - Query parameters (`GetCatalogSearchSuggestionsV1Params`):
 *   - `q`       — Free text to search for (required)
 *   - `section` — Section slug (e.g. `"gender_men"`) to scope all results to (optional)
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @param signal - Optional `AbortSignal` to cancel a superseded request.
 * @returns `SearchSuggestionsDto` on success, or `undefined` on bad request (400) or
 *   server error (500).
 */
export async function getCatalogSearchSuggestions(
  params: GetCatalogSearchSuggestionsV1Params,
  locale?: string,
  signal?: AbortSignal
): Promise<SearchSuggestionsDto | undefined> {
  const response = await getCatalogSearchSuggestionsV1(params, {
    headers: locale ? { 'Accept-Language': locale } : {},
    signal,
  });
  if (response.status !== 200) {
    console.warn(`getCatalogSearchSuggestions: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Fetches full product detail — images and variants — for the given public GUID.
 *
 * @param publicGuid - The product's public GUID.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns Full product detail, or `undefined` when not found (404) or on error (500).
 */
export async function getCatalogProductById(
  publicGuid: string,
  locale?: string
): Promise<ProductDetailDto | undefined> {
  const response = await getCatalogProductByGuidV1(publicGuid, {
    headers: locale ? { 'Accept-Language': locale } : {},
  });
  if (response.status !== 200) {
    console.warn(`getCatalogProductById: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Fetches products related to the given product from the dedicated catalog
 * endpoint. The server ranks by category + gender, preferring products
 * tagged "New" in Shopify, most recent first, excluding the source product.
 * No fallback: if the source product has no category/gender tag, or nothing
 * matches, the list is simply empty.
 *
 * @param publicGuid - Public GUID of the source product.
 * @param limit - Maximum number of products to return (API caps at 20).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns Related products, or `undefined` when the source product does not
 *   exist (404) or on error.
 */
export async function getRelatedProducts(
  publicGuid: string,
  limit: number,
  locale?: string
): Promise<ProductListItemDto[] | undefined> {
  const response = await getCatalogRelatedProductsV1(
    publicGuid,
    { limit },
    { headers: locale ? { 'Accept-Language': locale } : {} }
  );
  if (response.status !== 200) {
    console.warn(`getRelatedProducts: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Fetches related products for the "add to bag" placement from the dedicated
 * catalog endpoint. The server ranks by brand + gender, preferring products
 * tagged "New" in Shopify, falling back to most recent same-brand+gender
 * products when nothing is tagged "New". Excludes the source product.
 *
 * @param publicGuid - Public GUID of the source product.
 * @param limit - Maximum number of products to return (API caps at 20).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns Related products, or `undefined` when the source product does not
 *   exist (404) or on error.
 */
export async function getRelatedProductsForBag(
  publicGuid: string,
  limit: number,
  locale?: string
): Promise<ProductListItemDto[] | undefined> {
  const response = await getCatalogRelatedProductsForBagV1(
    publicGuid,
    { limit },
    { headers: locale ? { 'Accept-Language': locale } : {} }
  );
  if (response.status !== 200) {
    console.warn(`getRelatedProductsForBag: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Fetches stock levels from CPI for a given store and warehouse.
 *
 * @param params - Required query parameters:
 *   - `storeCode`     — Store identifier
 *   - `warehouseCode` — Warehouse identifier
 *   - `detailCode`    — Optional product detail code filter
 *   - `page`          — Page number (1-based)
 *   - `pageSize`      — Items per page
 * @returns Array of stock records, or `undefined` on bad request (400) or gateway error (502).
 */
export async function getStocks(params: GetStocksV1Params): Promise<CpiStockDto[] | undefined> {
  const response = await getStocksV1(params);
  if (response.status !== 200) {
    console.warn(`getStocks: unexpected status ${response.status}`);
    return undefined;
  }
  return response.data;
}

/**
 * Fetches a CMS category node by its content ID and returns it typed as
 * `CategoryContentResponseModel`.
 *
 * Delegates to `getContentById` from the CMS data layer, which handles
 * error logging and returns `undefined` on 404 or network failure.
 *
 * @param id - CMS node GUID of the category content item.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The category content model, or `undefined` when not found or on error.
 */
export async function getCategoryById(
  id: string,
  locale?: string
): Promise<CategoryContentResponseModel | undefined> {
  const content = await getContentById(id, locale);
  return content as CategoryContentResponseModel | undefined;
}

/**
 * Fetches a CMS subcategory node by its content ID and returns it typed as
 * `SubCategoryContentResponseModel`.
 *
 * Delegates to `getContentById` from the CMS data layer, which handles
 * error logging and returns `undefined` on 404 or network failure.
 *
 * @param id - CMS node GUID of the subcategory content item.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The subcategory content model, or `undefined` when not found or on error.
 */
export async function getSubCategoryById(
  id: string,
  locale?: string
): Promise<SubCategoryContentResponseModel | undefined> {
  const content = await getContentById(id, locale);
  return content as SubCategoryContentResponseModel | undefined;
}

/**
 * Fetches a CMS collection node by its content ID and returns it typed as
 * `CollectionContentResponseModel`.
 *
 * Delegates to `getContentById` from the CMS data layer, which handles
 * error logging and returns `undefined` on 404 or network failure.
 *
 * The collection response only carries shallow references in
 * `properties.associatedCategory`, so each picked category is re-fetched via
 * `getCategoryById` to return the fully populated category. Items without an
 * `id`, or whose category fails to resolve, are kept as returned by the
 * collection request.
 *
 * @param id - CMS node GUID of the collection content item.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The collection content model, or `undefined` when not found or on error.
 */
export async function getCollectionById(
  id: string,
  locale?: string
): Promise<CollectionContentResponseModel | undefined> {
  const collection = (await getContentById(id, locale)) as CollectionContentResponseModel | undefined;
  const associatedCategory = collection?.properties?.associatedCategory;
  if (!collection?.properties || !associatedCategory?.length) return collection;

  const categories = await Promise.all(
    associatedCategory.map(async category =>
      category.id ? ((await getCategoryById(category.id, locale)) ?? category) : category
    )
  );

  return {
    ...collection,
    properties: { ...collection.properties, associatedCategory: categories },
  };
}

/**
 * Fetches a CMS section node by its content ID and returns it typed as
 * `SectionContentResponseModel`.
 *
 * Delegates to `getContentById` from the CMS data layer, which handles
 * error logging and returns `undefined` on 404 or network failure.
 *
 * @param id - CMS node GUID of the section content item.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The section content model, or `undefined` when not found or on error.
 */
export async function getSectionById(
  id: string,
  locale?: string
): Promise<SectionContentResponseModel | undefined> {
  const content = await getContentById(id, locale);
  return content as SectionContentResponseModel | undefined;
}

/**
 * Fetches a shop section CMS page by its URL path.
 *
 * Delegates to `getPage` from the CMS data layer — the same generic route-resolution
 * mechanism used to resolve top-level screens — so it shares its path normalisation,
 * draft-mode preview header, and error/stub fallback behaviour.
 *
 * @param path - URL path to the shop section (e.g. `"en/shop/clothing"`).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The shop section content model typed as `ShopSectionContentResponseModel`,
 *   or `undefined` when not found or on error.
 */
export async function getShopSectionByPath(
  path: string,
  locale?: string
): Promise<ShopSectionContentResponseModel | undefined> {
  return getPage<ShopSectionContentResponseModel>(path, locale, false);
}

/**
 * Resolves the CMS section node picked for a given shop section page.
 *
 * A shop section page (`ShopSectionContentResponseModel`) references its section via
 * `properties.sectionPicker` — a single-item content picker. This composes
 * `getShopSectionByPath` to resolve the shop section page, then `getSectionById` to
 * fetch the full `SectionContentResponseModel` for the first picked item.
 *
 * @param path - URL path to the shop section (e.g. `"en/shop/clothing"`).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The section content model, or `undefined` when the shop section page is not
 *   found, its `sectionPicker` is empty, or the picked section fails to resolve.
 */
export async function getSectionByShopSectionPath(
  path: string,
  locale?: string
): Promise<SectionContentResponseModel | undefined> {
  const shopSection = await getShopSectionByPath(path, locale);
  const sectionId = shopSection?.properties?.sectionPicker?.[0]?.id;
  if (!sectionId) return undefined;
  return getSectionById(sectionId, locale);
}

/**
 * Fetches all shop section pages (e.g. Women, Men, Home) as direct children of the
 * `shop` repository node referenced by `appSettings.properties.shop.id`. Used to
 * resolve the list of section slugs available for navigation and URL matching.
 *
 * @param appSettings - App settings content, providing the `shop` repository id
 *   under `properties.shop.id`.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The shop section content models sorted by `sortOrder`, or `undefined` when
 *   `appSettings.properties.shop.id` is missing or the request fails.
 */
export async function getShopSections(
  appSettings?: AppSettingsContentResponseModel,
  culture?: string
): Promise<ShopSectionContentResponseModel[] | undefined> {
  try {
    const repositoryId = appSettings?.properties?.shop?.id;

    if (!repositoryId) {
      console.warn('getShopSections: shopRepository id is missing from appSettings');
      return undefined;
    }

    const filters = [`contentType:${ShopSectionContentResponseModelContentType.shopSection}`];
    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: filters,
        skip: 0,
        take: 9999,
        sort: ['sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as ShopSectionContentResponseModel[];
  } catch (error) {
    console.warn('getShopSections: request failed:', error);
    return undefined;
  }
}

/**
 * Resolves the slug of the shop section whose route the given pathname currently falls
 * under — e.g. so a search link opened from within `/en/shop/women/category_abc` can be
 * scoped to the "women" section.
 *
 * Matches `pathname` against every shop section page's `route.path` (via `getShopSections`),
 * then resolves the matched page's picked `Section` via `properties.sectionPicker` +
 * `getSectionById` (same resolution `getSectionByShopSectionPath` uses for a single page).
 *
 * @param appSettings - App settings content, providing the `shop` repository id (forwarded
 *   to `getShopSections`).
 * @param pathname - The current URL pathname, e.g. `/en/shop/women/category_abc`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The matched section's slug, or `undefined` when no shop section route matches
 *   `pathname`, the matched page has no `sectionPicker`, or the picked section fails to
 *   resolve.
 */
export async function getActiveShopSectionSlug(
  appSettings: AppSettingsContentResponseModel | undefined,
  pathname: string,
  locale?: string
): Promise<string | undefined> {
  const shopSections = await getShopSections(appSettings, locale);
  const matched = shopSections?.find(
    shopSection => shopSection.route?.path && pathname.includes(shopSection.route.path)
  );

  const sectionId = matched?.properties?.sectionPicker?.[0]?.id;
  if (!sectionId) return undefined;

  const section = await getSectionById(sectionId, locale);
  return section?.properties?.slug;
}

/**
 * Resolves the CMS section node picked for each shop section page.
 *
 * Fetches all shop section pages via `getShopSections`, then — for each page whose
 * `properties.sectionPicker` is set — resolves the picked item via `getSectionById`.
 * Shop sections with no `sectionPicker`, or whose picked section fails to resolve,
 * are skipped rather than included as `undefined`.
 *
 * @param appSettings - App settings content, providing the `shop` repository id under
 *   `properties.shop.id` — forwarded to `getShopSections`, and (when `fromPicker` is
 *   set) used directly to fetch the `shop` repository node itself.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @param fromPicker - When `true`, additionally fetches the `shop` repository node
 *   (`ShopIntroContentResponseModel`) and resolves the sections listed under its
 *   `sectionItems` component's `sectionList` (via each item's `properties.section.id`
 *   + `getSectionById`), returning those instead of the `sectionPicker` resolution
 *   below. Falls through to the `sectionPicker` resolution when the `shop` node or
 *   its `sectionItems` component cannot be resolved.
 * @returns The resolved section content models — from the `sectionItems` picker when
 *   `fromPicker` is `true` and resolves, otherwise from each shop section page's
 *   `sectionPicker` — or `undefined` when `getShopSections` returns `undefined` or
 *   `appSettings.properties.shop.id` is missing.
 */
export async function getSections(
  appSettings?: AppSettingsContentResponseModel,
  culture?: string,
  fromPicker: boolean = false
): Promise<SectionContentResponseModel[] | undefined> {
  const shopSections = await getShopSections(appSettings, culture);
  if (!shopSections) return undefined;

  const repositoryId = appSettings?.properties?.shop?.id;

  if (!repositoryId) {
    console.warn('getSections: shopRepository id is missing from appSettings');
    return undefined;
  }

  if (fromPicker) {
    const shop = (await getContentById(repositoryId, culture)) as ShopIntroContentResponseModel;

    if (shop) {
      const sectionItemsComponent = shop?.properties?.components?.items
        ? shop?.properties?.components?.items
            .filter(
              item =>
                item.content &&
                item.content?.contentType == SectionItemsElementContentModelContentType.sectionItems
            )
            .map(item => item.content as SectionItemsElementContentModel)?.[0]
        : undefined;

      if (sectionItemsComponent) {
        const sectionList = sectionItemsComponent?.properties?.sectionList?.items ?? [];
        const sectionItems = sectionList
          .filter(item => item.content)
          .map(item => item.content as SectionItemElementContentModel);

        if (sectionItems) {
          const pickedSections = await Promise.all(
            sectionItems
              .filter(item => item.contentType == SectionItemElementContentModelContentType.sectionItem)
              .map(sectionItem => {
                const sectionId = sectionItem?.properties?.section?.id;
                if (!sectionId) return undefined;
                return getSectionById(sectionId, culture);
              })
          );
          return pickedSections.filter((section): section is SectionContentResponseModel => Boolean(section));
        }
      }
    }
  }

  const sections = await Promise.all(
    shopSections.map(shopSection => {
      const sectionId = shopSection.properties?.sectionPicker?.[0]?.id;
      if (!sectionId) return undefined;
      return getSectionById(sectionId, culture);
    })
  );

  return sections.filter((section): section is SectionContentResponseModel => Boolean(section));
}

/**
 * Resolves the popular-search entries configured on app settings.
 *
 * App settings expose popular searches via `properties.popularSearches` — a CMS block
 * list — whose items carry the popular-search content under `.content`. This extracts
 * and types that content, skipping any item with no `content`.
 *
 * @param appSettings - App settings content, providing the block list under
 *   `properties.popularSearches.items`.
 * @returns The popular-search content models, or `[]` when `appSettings` or
 *   `properties.popularSearches.items` is missing.
 */
export function getPopularSearches(
  appSettings?: AppSettingsContentResponseModel
): ShopPopularSearchesElementContentModel[] {
  const items = appSettings?.properties?.popularSearches?.items ?? [];

  return items
    .filter(item => item.content)
    .map(item => item.content as ShopPopularSearchesElementContentModel);
}

/**
 * Fetches all subcategories that are direct children of the given category node.
 *
 * Queries the CMS Delivery API for children of `categoryId` filtered by the
 * `subCategory` content type. Returns all matching items cast to
 * `SubCategoryContentResponseModel`.
 *
 * @param categoryId - CMS node GUID of the parent category.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns Array of subcategory content models, or `undefined` on error.
 */
export async function getSubCategoriesByCategoryId(
  categoryId: string,
  locale?: string
): Promise<SubCategoryContentResponseModel[] | undefined> {
  const result = await getContents(
    {
      fetch: `children:${categoryId}`,
      filter: [`contentType:${SubCategoryContentResponseModelContentType.subCategory}`],
      skip: 0,
      take: 9999,
    },
    locale
  );
  if (!result) return undefined;
  return (result.items ?? []) as SubCategoryContentResponseModel[];
}

/**
 * Fetches all sub-subcategories that are direct children of the given subcategory node.
 *
 * Queries the CMS Delivery API for children of `subCategoryId` filtered by the
 * `subSubCategory` content type. Returns all matching items cast to
 * `SubSubCategoryContentResponseModel`.
 *
 * @param subCategoryId - CMS node GUID of the parent subcategory.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns Array of sub-subcategory content models, or `undefined` on error.
 */
export async function getSubSubCategoriesBySubCategoryId(
  subCategoryId: string,
  locale?: string
): Promise<SubSubCategoryContentResponseModel[] | undefined> {
  const result = await getContents(
    {
      fetch: `children:${subCategoryId}`,
      filter: [`contentType:${SubSubCategoryContentResponseModelContentType.subSubCategory}`],
      skip: 0,
      take: 9999,
    },
    locale
  );
  if (!result) return undefined;
  return (result.items ?? []) as SubSubCategoryContentResponseModel[];
}

/**
 * Fetches brand nodes from the CMS brands repository defined in app settings.
 *
 * Queries children of the `brandsRepository` node filtered by the `brand` content
 * type, sorted alphabetically by name then by `sortOrder`. All filter params are
 * optional and additive — omitting them returns the full brand list.
 *
 * @param appSettings - App settings content model; must contain `brandsRepository.id`.
 * @param culture - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` and used to build the section slug filter field name.
 * @param section - Optional section slug to filter brands. When provided, adds a
 *   `property:sections.slug_<culture>:<section>` filter to the query.
 * @param isOurBrand - When `true`, restricts results to brands flagged as "our brands".
 * @param isTopBrand - When `true`, restricts results to brands flagged as "top brands".
 * @returns Array of brand content models, or `undefined` when `brandsRepository` is
 *   missing from app settings or the request fails.
 */
export async function getShopBrands(
  appSettings: AppSettingsContentResponseModel,
  culture?: string,
  section?: string,
  isOurBrand?: boolean,
  isTopBrand?: boolean
): Promise<BrandContentResponseModel[] | undefined> {
  try {
    const repositoryId = appSettings.properties?.brandsRepository?.id;

    if (!repositoryId) {
      console.warn('getShopBrands: brandsRepository id is missing from appSettings');
      return undefined;
    }

    const filters = []; //[`contentType:${BrandContentResponseModelContentType.brand}`];

    if (section) {
      filters.push(`property:sections.slug_${culture}:${section}`);
    }
    if (isOurBrand) {
      filters.push(`property:isOurBrand:1`);
    }
    if (isTopBrand) {
      filters.push(`property:isTopBrand:1`);
    }

    const result = await getContents(
      {
        fetch: `children:${repositoryId}`,
        filter: filters,
        skip: 0,
        take: 9999,
        sort: ['name:asc', 'sortOrder:asc'],
      },
      culture
    );

    return (result?.items ?? []) as BrandContentResponseModel[];
  } catch (error) {
    console.warn('getShopBrands: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches a CMS brand node by its content ID and returns it typed as
 * `BrandContentResponseModel`.
 *
 * Delegates to `getContentById` from the CMS data layer, which handles
 * error logging and returns `undefined` on 404 or network failure.
 *
 * @param id - CMS node GUID of the brand content item.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns The brand content model, or `undefined` when not found or on error.
 */
export async function getBrandById(
  id: string,
  locale?: string
): Promise<BrandContentResponseModel | undefined> {
  const content = await getContentById(id, locale);
  return content as BrandContentResponseModel | undefined;
}
